import { useEffect, useRef, useState } from "react";
import { api } from "@/App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Upload, Trash2, Image as ImageIcon, AlertCircle, Mail } from "lucide-react";
import { useBranding } from "@/hooks/useBranding";

const MAX_SIZE_KB = 500;

function LogoUploader({ label, description, currentUrl, onUpload, onDelete, testIdPrefix }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== "image/png") {
      toast.error("Only PNG files are allowed");
      e.target.value = "";
      return;
    }
    if (file.size > MAX_SIZE_KB * 1024) {
      toast.error(`File too large. Max ${MAX_SIZE_KB}KB`);
      e.target.value = "";
      return;
    }
    setBusy(true);
    try {
      await onUpload(file);
      toast.success(`${label} uploaded`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Remove ${label}?`)) return;
    setBusy(true);
    try {
      await onDelete();
      toast.success(`${label} removed`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-white" data-testid={`${testIdPrefix}-row`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-4 flex-1 min-w-0">
          <div className="w-20 h-20 bg-gray-50 border border-gray-200 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
            {currentUrl ? (
              <img
                src={currentUrl}
                alt={label}
                className="max-w-full max-h-full object-contain"
                data-testid={`${testIdPrefix}-preview`}
              />
            ) : (
              <ImageIcon className="w-8 h-8 text-gray-300" />
            )}
          </div>
          <div className="min-w-0">
            <p className="font-medium text-gray-900 truncate">{label}</p>
            {description && <p className="text-xs text-gray-500 mt-0.5">{description}</p>}
            <p className="text-xs text-gray-400 mt-1">PNG only · Max {MAX_SIZE_KB}KB</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <input
            ref={inputRef}
            type="file"
            accept="image/png"
            className="hidden"
            onChange={handleFile}
            data-testid={`${testIdPrefix}-input`}
          />
          <Button
            size="sm"
            variant="outline"
            onClick={() => inputRef.current?.click()}
            disabled={busy}
            data-testid={`${testIdPrefix}-upload-btn`}
          >
            <Upload className="w-4 h-4 mr-1" />
            {currentUrl ? "Replace" : "Upload"}
          </Button>
          {currentUrl && (
            <Button
              size="sm"
              variant="outline"
              onClick={handleDelete}
              disabled={busy}
              className="text-red-600 hover:text-red-700"
              data-testid={`${testIdPrefix}-delete-btn`}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function BrandingPanel() {
  const { getMainLogoUrl, getMarketLogoUrl, refreshBranding, mainLogoPath } = useBranding();
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [emailLogoEnabled, setEmailLogoEnabled] = useState(false);
  const [savingToggle, setSavingToggle] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [unitsRes, brandingRes] = await Promise.all([
          api.get("/units"),
          api.get("/branding"),
        ]);
        setUnits(unitsRes.data || []);
        setEmailLogoEnabled(!!brandingRes.data?.email_header_logo_enabled);
      } catch (e) {
        console.error("Failed to load branding panel data:", e);
        toast.error("Could not load branding data");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const toggleEmailLogo = async (next) => {
    setSavingToggle(true);
    try {
      await api.put("/admin/branding/settings", { email_header_logo_enabled: next });
      setEmailLogoEnabled(next);
      toast.success(next ? "Logo will appear in email headers" : "Logo removed from email headers");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update setting");
    } finally {
      setSavingToggle(false);
    }
  };

  const uploadMain = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    await api.post("/admin/branding/main-logo", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    await refreshBranding();
  };

  const deleteMain = async () => {
    await api.delete("/admin/branding/main-logo");
    await refreshBranding();
  };

  const uploadMarket = (marketId) => async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    await api.post(`/admin/branding/market-logo/${encodeURIComponent(marketId)}`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    await refreshBranding();
  };

  const deleteMarket = (marketId) => async () => {
    await api.delete(`/admin/branding/market-logo/${encodeURIComponent(marketId)}`);
    await refreshBranding();
  };

  return (
    <div className="space-y-6" data-testid="branding-panel">
      <Card className="bg-white border-gray-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ImageIcon className="w-5 h-5 text-indigo-600" />
            Main App Logo
          </CardTitle>
          <p className="text-sm text-gray-600">
            Shown on the login page, sidebar, and market selection header.
          </p>
        </CardHeader>
        <CardContent>
          <LogoUploader
            label="Efacilicare Main Logo"
            description="Square PNG works best (e.g. 512×512)."
            currentUrl={getMainLogoUrl()}
            onUpload={uploadMain}
            onDelete={deleteMain}
            testIdPrefix="branding-main"
          />

          <div className="mt-4 flex items-start justify-between gap-4 p-4 rounded-lg border border-gray-200 bg-gray-50" data-testid="email-header-logo-toggle-row">
            <div className="flex items-start gap-3 min-w-0">
              <Mail className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
              <div className="min-w-0">
                <p className="font-medium text-gray-900">Show logo in email headers</p>
                <p className="text-sm text-gray-600 mt-0.5">
                  When on, the main logo is embedded at the top of every notification email
                  (approvals, rejections, account updates).
                </p>
                {!mainLogoPath && (
                  <p className="text-xs text-amber-600 mt-1.5 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    Upload a main logo first to use this.
                  </p>
                )}
              </div>
            </div>
            <Switch
              checked={emailLogoEnabled}
              onCheckedChange={toggleEmailLogo}
              disabled={savingToggle || !mainLogoPath}
              data-testid="email-header-logo-toggle"
            />
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white border-gray-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ImageIcon className="w-5 h-5 text-indigo-600" />
            Market Logos
          </CardTitle>
          <p className="text-sm text-gray-600">
            Each market gets its own logo, shown on the market selection cards and after a market
            is selected (sidebar header).
          </p>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-8 text-center text-gray-500">Loading markets…</div>
          ) : units.length === 0 ? (
            <div className="py-8 text-center text-gray-500 flex flex-col items-center gap-2">
              <AlertCircle className="w-8 h-8 text-gray-300" />
              <p>No business units found. Create one in the Units tab first.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {units.map((u) => (
                <LogoUploader
                  key={u.id}
                  label={u.name}
                  description={u.full_name || ""}
                  currentUrl={getMarketLogoUrl(u.id) || getMarketLogoUrl(u.name)}
                  onUpload={uploadMarket(u.id)}
                  onDelete={deleteMarket(u.id)}
                  testIdPrefix={`branding-market-${u.id}`}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
